import React, { useState } from 'react';
import {
  Box, Button, Paper, TextField, Typography,
} from '@mui/material';
import { realNumberRegex } from '../../../utilities/regex';
import {
  DigitalGroundTruthGroup, DigitalSection, DigitalSectionScore, DigitalSurveyScore,
} from '../../../redux/types';

type Props = {
  section: DigitalSection,
  gtGroup: DigitalGroundTruthGroup | null,
  digitalSectionScores?: DigitalSectionScore[];
  digitalSurveyScores?: DigitalSurveyScore[];
  addOrUpdateDigitalSectionScore: (payload: Omit<DigitalSectionScore, 'id'>) => void;
  addOrUpdateDigitalSurveyScore: (payload: Omit<DigitalSurveyScore, 'id'>) => void;
};

type State = {
  text: string
  error: boolean;
  saved: boolean;
}

const ScoreEditor: React.FC<Props> = ({
  section,
  gtGroup,
  digitalSectionScores,
  digitalSurveyScores,
  addOrUpdateDigitalSectionScore,
  addOrUpdateDigitalSurveyScore,
}: Props) => {
  const selectedSectionScore = digitalSectionScores
    ?.find((s) => s.digitalSectionId === section.id && s.digitalGroundTruthGroupId === gtGroup?.id);

  const selectedSurveyScore = digitalSurveyScores
    ?.find((s) => s.digitalGroundTruthGroupId === gtGroup?.id);

  const [sectionState, setSectionState] = useState<State>({
    text: selectedSectionScore?.passScore?.toString() || '',
    error: false,
    saved: false,
  });
  const [surveyState, setSurveyState] = useState<State>({
    text: selectedSurveyScore?.passScore?.toString() || '',
    error: false,
    saved: false,
  });

  const [prevSectionScore, setPrevSectionScore] = useState(digitalSectionScores);
  const [prevSurveyScore, setPrevSurveyScore] = useState(digitalSurveyScores);
  const [showSecScoreSaveBtn, setShowSecScoreSaveBtn] = useState(false);
  const [showSurveyScoreSaveBtn, setShowSurveyScoreSaveBtn] = useState(false);

  if (digitalSectionScores !== prevSectionScore) {
    setPrevSectionScore(digitalSectionScores);
    setSectionState({
      text: selectedSectionScore?.passScore?.toString() || '',
      error: false,
      saved: false,
    });
  }

  if (digitalSurveyScores !== prevSurveyScore) {
    setPrevSurveyScore(digitalSurveyScores);
    setSurveyState({
      text: selectedSurveyScore?.passScore?.toString() || '',
      error: false,
      saved: false,
    });
  }

  const onSectionScoreChange = () => {
    const val = sectionState.text;
    if (!selectedSectionScore) {
      return;
    }
    const test = realNumberRegex.test(val);
    if (test) {
      const { maxScore } = selectedSectionScore;
      if (maxScore && (Number(val) > maxScore)) {
        setSectionState({
          text: val,
          error: true,
          saved: false,
        });
        return;
      }

      addOrUpdateDigitalSectionScore({
        ...selectedSectionScore,
        passScore: Number(val),
      });
      setSectionState({
        text: val,
        error: false,
        saved: true,
      });
      setShowSecScoreSaveBtn(false);
    } else {
      setSectionState({
        text: '',
        error: true,
        saved: false,
      });
    }
  };

  const onSurveyScoreChange = () => {
    const val = surveyState.text;
    if (!selectedSurveyScore) {
      return;
    }
    const test = realNumberRegex.test(val);
    if (test) {
      const { maxScore } = selectedSurveyScore;
      if (maxScore && (Number(val) > maxScore)) {
        setSurveyState({
          text: val,
          error: true,
          saved: false,
        });
        return;
      }

      addOrUpdateDigitalSurveyScore({
        ...selectedSurveyScore,
        passScore: Number(val),
      });
      setSurveyState({
        text: val,
        error: false,
        saved: true,
      });
      setShowSurveyScoreSaveBtn(false);
    } else {
      setSurveyState({
        text: '',
        error: true,
        saved: false,
      });
    }
  };

  // const sectionScoreDebouncer = useDebounce<string>(onSectionScoreChange, 500);
  // const surveyScoreDebouncer = useDebounce<string>(onSurveyScoreChange, 500);

  const onSectionTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSectionState({
      text: e.target.value,
      error: false,
      saved: false,
    });
    setShowSecScoreSaveBtn(true);
  };

  const onSurveyTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSurveyState({
      text: e.target.value,
      error: false,
      saved: false,
    });
    setShowSurveyScoreSaveBtn(true);
  };

  if (!digitalSectionScores || !digitalSurveyScores) {
    return null;
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 1 }}>
        <Box sx={{ my: 1 }} display="flex" alignItems="center">
          <Typography sx={{ flexBasis: 230 }} fontWeight={500}>
            Section scores
          </Typography>
          <Box sx={{ gap: 1 }} display="flex" alignItems="center">
            <TextField
              value={sectionState.text}
              label="Pass score"
              onChange={onSectionTextChange}
              error={sectionState.error}
            />
            {selectedSectionScore?.maxScore !== undefined && (
              <TextField
                value={selectedSectionScore.maxScore}
                label="Possible Max Score"
                disabled
              />
            )}
          </Box>
          {
            showSecScoreSaveBtn && (
              <Button
                variant="outlined"
                sx={{ mx: 2 }}
                onClick={() => {
                  onSectionScoreChange();
                }}
              >
                Save
              </Button>
            )
          }
          {
            sectionState.saved && (
              <Typography sx={{ mx: 2 }} variant="caption">Saved</Typography>
            )
          }
        </Box>
      </Paper>
      <Paper elevation={0} sx={{ p: 2 }}>
        <Box sx={{ my: 1 }} display="flex" alignItems="center">
          <Typography sx={{ flexBasis: 230 }} fontWeight={500}>Survey scores</Typography>
          <Box sx={{ gap: 1 }} display="flex" alignItems="center">
            <TextField
              value={surveyState.text}
              label="Pass score"
              onChange={onSurveyTextChange}
              error={surveyState.error}
            />
            {selectedSurveyScore?.maxScore !== undefined && (
              <TextField
                value={selectedSurveyScore.maxScore}
                label="Possible Max Survey Score"
                disabled
              />
            )}
          </Box>
          {
            showSurveyScoreSaveBtn && (
              <Button
                variant="outlined"
                sx={{ mx: 2 }}
                onClick={() => {
                  onSurveyScoreChange();
                }}
              >
                Save
              </Button>
            )
          }
          {
            surveyState.saved && (
              <Typography sx={{ mx: 2 }} variant="caption">Saved</Typography>
            )
          }
        </Box>
      </Paper>
    </Box>
  );
};

export default ScoreEditor;
